import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { CONDITION_OPTIONS, PACK_CODE_OPTIONS, CONDITION_DETAIL_OPTIONS, DAMAGE_LOCATION_OPTIONS } from "@/lib/formatters";
import { Plus, Trash2, PackageCheck, Copy } from "lucide-react";

const _findLabel = (options, value) => {
  if (!value) return "";
  const opt = options.find((o) => o.value === value);
  if (!opt) return value;
  // Le option dei dropdown hanno label "CODICE - Descrizione". Nel banner ci
  // serve solo la parte umana, quindi tagliamo il prefisso "XX - " quando
  // corrisponde al value dell'option.
  const prefix = `${value} - `;
  if (opt.label.startsWith(prefix)) return opt.label.slice(prefix.length);
  return opt.label;
};

/**
 * Restituisce la descrizione testuale dello "Stato al ritiro" snapshottato
 * sull'item della release. Null se non c'è nulla di rilevante da mostrare
 * (item era "buono" senza note/danni).
 */
function formatOriginalStatus(it) {
  const cond = (it.original_condition || "").toLowerCase();
  const detail = it.original_condition_detail || "";
  const loc = it.original_damage_location || "";
  const notes = (it.original_damage_notes || "").trim();
  const hasExtra = !!(detail || loc || notes);
  if ((!cond || cond === "buono") && !hasExtra) return null;
  const parts = [];
  if (cond) parts.push(_findLabel(CONDITION_OPTIONS, cond));
  if (detail) parts.push(_findLabel(CONDITION_DETAIL_OPTIONS, detail));
  if (loc) parts.push(`pos. ${_findLabel(DAMAGE_LOCATION_OPTIONS, loc)}`);
  let base = parts.filter(Boolean).join(" · ");
  if (notes) base = base ? `${base} — ${notes}` : notes;
  return base || null;
}

export function InventoryItemsList({ form, isEditable, addItem, removeItem, updateItem, updateItemFields }) {
  const totalItems = form.items.length;
  return (
    <div className="card-surface p-4 sm:p-5 space-y-4">
      <div className="flex items-center justify-between">
        <h2 className="text-sm font-semibold font-['Outfit'] text-zinc-500 uppercase tracking-wider flex items-center gap-2">
          Elementi
          <span className="text-[11px] font-medium text-zinc-400 normal-case">({totalItems})</span>
        </h2>
        {isEditable && (
          <Button
            size="sm"
            variant="outline"
            onClick={addItem}
            className="rounded-sm h-9 sm:h-8 px-3 sm:px-2.5"
            data-testid="add-inv-item-btn"
          >
            <Plus className="w-4 h-4 sm:w-3 sm:h-3 mr-1" /> Aggiungi
          </Button>
        )}
      </div>
      <div className="space-y-3">
        {form.items.map((it, idx) => {
          const originalStatus = formatOriginalStatus(it);
          return (
          <div
            key={it.id || idx}
            className="border border-zinc-200 rounded-sm p-3 space-y-2.5"
            data-testid={`inv-item-${idx}`}
          >
            {/* Row 1: index + description */}
            <div className="flex gap-2 items-start">
              <span className="text-[11px] font-mono text-zinc-400 mt-3 w-6 text-right shrink-0 select-none">#{idx + 1}</span>
              <Input
                placeholder="Descrizione"
                value={it.description}
                onChange={(e) => updateItem(idx, "description", e.target.value)}
                disabled={!isEditable}
                className="rounded-sm flex-1 h-11 sm:h-10 text-base sm:text-sm"
                data-testid={`inv-item-${idx}-desc`}
              />
            </div>
            {/* Banner "Stato al ritiro" — solo per items della release con snapshot originale */}
            {originalStatus && (
              <div
                className="ml-8 sm:ml-0 px-2.5 py-1.5 rounded-sm border border-amber-300 bg-amber-50/60 flex items-start gap-1.5 text-[11px] sm:text-xs leading-snug"
                data-testid={`inv-item-${idx}-original-status`}
                title="Stato registrato in fase di ritiro al deposito"
              >
                <PackageCheck className="w-3.5 h-3.5 text-amber-700 shrink-0 mt-0.5" />
                <span className="text-amber-900 flex-1">
                  <span className="font-semibold uppercase tracking-wider text-[10px] mr-1">Al ritiro:</span>
                  {originalStatus}
                </span>
                {isEditable && (
                  <button
                    type="button"
                    onClick={() => {
                      updateItemFields(idx, {
                        condition: it.original_condition || "buono",
                        condition_detail: it.original_condition_detail || "",
                        damage_location: it.original_damage_location || "",
                        damage_notes: it.original_damage_notes || "",
                      });
                    }}
                    className="shrink-0 inline-flex items-center gap-1 px-1.5 py-0.5 rounded-sm text-amber-800 hover:bg-amber-100/80 active:bg-amber-200/80 transition text-[10px] font-semibold uppercase tracking-wider"
                    data-testid={`inv-item-${idx}-copy-original-btn`}
                    title="Copia lo stato del ritiro nei campi sottostanti"
                  >
                    <Copy className="w-3 h-3" />
                    Copia
                  </button>
                )}
              </div>
            )}
            {/* Row 2: quantity + delete (mobile-friendly tap targets) */}
            <div className="flex gap-2 items-center pl-8">
              <Input
                type="number"
                inputMode="decimal"
                placeholder="Qtà"
                value={it.quantity}
                onChange={(e) => updateItem(idx, "quantity", parseFloat(e.target.value) || 0)}
                disabled={!isEditable}
                className="rounded-sm w-24 h-11 sm:h-10 text-base sm:text-sm"
                data-testid={`inv-item-${idx}-qty`}
              />
              <span className="text-[11px] text-zinc-400">pz</span>
              {isEditable && form.items.length > 1 && (
                <button
                  onClick={() => removeItem(idx)}
                  className="ml-auto p-2.5 hover:bg-red-50 active:bg-red-100 rounded-sm flex items-center gap-1.5 text-red-500"
                  aria-label="Rimuovi elemento"
                  data-testid={`inv-item-${idx}-delete`}
                >
                  <Trash2 className="w-4 h-4" />
                  <span className="text-xs sm:hidden">Rimuovi</span>
                </button>
              )}
            </div>
            {/* Row 3: classifications grid */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 pl-8 sm:pl-0">
              <Select value={it.pack_code || "_none"} onValueChange={(v) => updateItem(idx, "pack_code", v === "_none" ? "" : v)} disabled={!isEditable}>
                <SelectTrigger className="rounded-sm h-11 sm:h-10 text-sm" data-testid={`inv-item-${idx}-pack`}><SelectValue placeholder="Cod. Imballo" /></SelectTrigger>
                <SelectContent>{PACK_CODE_OPTIONS.map(o => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}</SelectContent>
              </Select>
              <Select value={it.condition || "buono"} onValueChange={(v) => updateItem(idx, "condition", v)} disabled={!isEditable}>
                <SelectTrigger className="rounded-sm h-11 sm:h-10 text-sm" data-testid={`inv-item-${idx}-condition`}><SelectValue /></SelectTrigger>
                <SelectContent>{CONDITION_OPTIONS.map(o => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}</SelectContent>
              </Select>
              <Select value={it.condition_detail || "_none"} onValueChange={(v) => updateItem(idx, "condition_detail", v === "_none" ? "" : v)} disabled={!isEditable}>
                <SelectTrigger className="rounded-sm h-11 sm:h-10 text-sm" data-testid={`inv-item-${idx}-detail`}><SelectValue placeholder="Dettaglio" /></SelectTrigger>
                <SelectContent>{CONDITION_DETAIL_OPTIONS.map(o => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}</SelectContent>
              </Select>
              <Select value={it.damage_location || "_none"} onValueChange={(v) => updateItem(idx, "damage_location", v === "_none" ? "" : v)} disabled={!isEditable}>
                <SelectTrigger className="rounded-sm h-11 sm:h-10 text-sm" data-testid={`inv-item-${idx}-location`}><SelectValue placeholder="Posizione" /></SelectTrigger>
                <SelectContent>{DAMAGE_LOCATION_OPTIONS.map(o => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            {/* Row 4: damage notes */}
            <div className="pl-8 sm:pl-0">
              <Input
                placeholder="Note danni"
                value={it.damage_notes || ""}
                onChange={(e) => updateItem(idx, "damage_notes", e.target.value)}
                disabled={!isEditable}
                className="rounded-sm h-11 sm:h-10 text-base sm:text-sm"
                data-testid={`inv-item-${idx}-notes`}
              />
              {it.from_quote && <span className="text-[10px] text-zinc-400 uppercase tracking-wider mt-1 inline-block">Da preventivo</span>}
            </div>
          </div>
          );
        })}
      </div>
      {/* Sticky-ish big add button on mobile - secondary at bottom of list */}
      {isEditable && (
        <Button
          type="button"
          variant="outline"
          onClick={addItem}
          className="md:hidden w-full rounded-sm h-12 border-dashed border-[#002FA7]/40 text-[#002FA7] hover:bg-blue-50/40"
          data-testid="add-inv-item-btn-bottom"
        >
          <Plus className="w-5 h-5 mr-1" /> Aggiungi elemento
        </Button>
      )}
    </div>
  );
}
